import { useState } from 'react'
import { Alert, App, Badge, Button, Card, Modal, Space, Typography } from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { fetchTelegramLinkStatus, issueTelegramLinkCode, unlinkTelegram, type LinkCode } from './notifyApi'

interface TelegramLinkPanelProps {
  subscriberId: string
  disabled?: boolean
}

/**
 * Telegram bog'lash — abonent kartochkasidagi panel.
 *
 * Bot birinchi bo'lib yoza olmaydi (`notify/README.md` "Telegram bog'lash
 * oqimi"), shuning uchun oqim: xodim BIR MARTALIK kod chiqaradi → abonent
 * botga `/start <kod>` yozadi → 15 daqiqa ichida (yoki sozlangan muddatda)
 * bog'lanadi. Kod TAKROR ko'rsatilmaydi — chiqarilgan zahoti ekranda,
 * keyin faqat "bog'langan/bog'lanmagan" holati qoladi.
 */
export function TelegramLinkPanel({ subscriberId, disabled }: TelegramLinkPanelProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const [issuedCode, setIssuedCode] = useState<LinkCode | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirmUnlink, setConfirmUnlink] = useState(false)

  const status = useQuery({
    queryKey: queryKeys.notify.telegramLink(subscriberId),
    queryFn: () => fetchTelegramLinkStatus(subscriberId),
    /*
      Bog'lanish holati kunlab o'zgarmaydi va uni panel o'zi o'zgartirsa
      keshni qo'lda bekor qiladi (`unlink` → `invalidateQueries`).
      Abonent botga `/start` yozgan payt esa panelga baribir darhol
      yetmaydi — buni "Yangilash" yoki bo'limga qayta kirish hal qiladi.
      Umumiy 10 soniyalik chegara bu yerda faqat ortiqcha so'rov edi.
    */
    staleTime: 5 * 60_000,
  })

  const issue = useMutation({
    mutationFn: () => issueTelegramLinkCode(subscriberId),
    onSuccess: (code) => {
      setIssuedCode(code)
      setError(null)
    },
    onError: (err) => setError(translateError(err, t)),
  })

  const unlink = useMutation({
    mutationFn: () => unlinkTelegram(subscriberId),
    onSuccess: () => {
      setConfirmUnlink(false)
      void queryClient.invalidateQueries({ queryKey: queryKeys.notify.telegramLink(subscriberId) })
      // X-SIB-004 — bog'lanish uzilgani faqat kartadagi holat tegining
      // o'zgarishidan bilinardi; uni payqamaslik oson, chunki teg
      // sahifaning boshqa joyida
      void message.success(t('common.toastDeleted'))
    },
    // ⚠️ `onError` ATAYLAB yo'q: bu mutatsiya TASDIQ OYNASIGA bog'langan
    // (`confirmLoading={unlink.isPending}`) va uning xatosi kartaning
    // yuqorisidagi bannerga chiqsa, oyna niqobi ortida qolardi — operator
    // faqat spinner to'xtaganini ko'rardi. `issue` esa oynasiz, uning
    // xatosi bannerda to'g'ri joyda.
  })

  /** Bekor qilib chiqish — xato xabari ham qolmasin */
  const closeUnlink = () => {
    unlink.reset()
    setConfirmUnlink(false)
  }

  return (
    <Card title={t('notify.telegram.title')}>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
        {t('notify.telegram.explain')}
      </Typography.Paragraph>

      {error ? (
        <Alert
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          message={error}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      <Space direction="vertical" style={{ width: '100%' }}>
        <Badge
          status={status.data ? 'success' : 'default'}
          text={status.data ? t('notify.telegram.linked') : t('notify.telegram.notLinked')}
        />

        {!disabled ? (
          <Can permission="notify.telegram.link">
            <Space wrap>
              <Button loading={issue.isPending} onClick={() => issue.mutate()}>
                {t('notify.telegram.issueCode')}
              </Button>
              {status.data ? (
                <Button danger onClick={() => setConfirmUnlink(true)}>
                  {t('notify.telegram.unlink')}
                </Button>
              ) : null}
            </Space>
          </Can>
        ) : null}
      </Space>

      <Modal
        open={issuedCode !== null}
        title={t('notify.telegram.codeTitle')}
        onCancel={() => setIssuedCode(null)}
        footer={
          <Button type="primary" onClick={() => setIssuedCode(null)}>
            {t('common.close')}
          </Button>
        }
        destroyOnHidden
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('notify.telegram.codeOneTimeTitle')}
          description={t('notify.telegram.codeOneTimeText')}
        />
        {issuedCode ? (
          <Space direction="vertical" size={4}>
            <Typography.Text>{t('notify.telegram.codeInstruction')}</Typography.Text>
            <Typography.Text code copyable style={{ fontSize: 16 }}>
              /start {issuedCode.fullCode}
            </Typography.Text>
            <Typography.Text type="secondary" title={utcTooltip(issuedCode.expiresAt)}>
              {t('notify.telegram.expiresAt', { time: formatDateTime(issuedCode.expiresAt) })}
            </Typography.Text>
          </Space>
        ) : null}
      </Modal>

      <Modal
        open={confirmUnlink}
        title={t('notify.telegram.unlinkTitle')}
        onCancel={closeUnlink}
        onOk={() => unlink.mutate()}
        confirmLoading={unlink.isPending}
        okText={t('notify.telegram.unlink')}
        okButtonProps={{ danger: true }}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        {t('notify.telegram.unlinkConfirm')}

        {unlink.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginTop: 16 }}
            message={translateError(unlink.error, t)}
          />
        ) : null}
      </Modal>
    </Card>
  )
}
